import { usePubSub } from "@videosdk.live/react-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";

// Live view of the customer's room measurement during the call.
// The customer web app (dev.customer-connect.satsure.co/call) publishes on the
// VideoSDK PubSub topic "MEASUREMENT" (scoped to this meeting):
//   { type: "state", active, mode: "ar"|"estimate", rooms:[{name, areaM2, perimM, edgesM[], poly:[[x,y]...]}],
//     current:{corners, areaM2, poly}, totalM2, ts }
//   { type: "image", name, ts }   -> a new capture was uploaded to the gallery
// Agent buttons are sent back on "MEASUREMENT_CMD".

const API_BASE = "https://dev.api.customer-connect.satsure.co/api/v1";
const M2_TO_FT2 = 10.7639;
const MEASURE_IMG = /(ar_measurement|depth_measurement|photo|screenshot)_/;

// Latest customer state, readable outside the panel (the screenshot button checks it).
let latestState = null;
export const getLatestMeasureState = () => latestState;
// Ask the panel to reload the captures list now (e.g. right after the agent's screenshot).
export const refreshCaptures = () => window.dispatchEvent(new Event("cc-captures-changed"));

const sqft = (m2) => (m2 * M2_TO_FT2).toFixed(1);
function ftIn(m) {
  const inch = Math.round(m / 0.0254);
  return Math.floor(inch / 12) + "′ " + (inch % 12) + "″";
}

function RoomShape({ poly, edgesM, size = 120 }) {
  if (!poly || poly.length < 2) return null;
  const xs = poly.map((p) => p[0]), ys = poly.map((p) => p[1]);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  const span = Math.max(Math.max(...xs) - minX, Math.max(...ys) - minY) || 1;
  const pad = 22, k = (size - 2 * pad) / span;
  const pts = poly.map(([x, y]) => [pad + (x - minX) * k, pad + (y - minY) * k]);
  const closed = poly.length >= 3;
  return (
    <svg width={size} height={size} style={{ background: "#f4f7f9", borderRadius: 6, flex: "0 0 auto" }}>
      <polygon
        points={pts.map((p) => p.join(",")).join(" ")}
        fill={closed ? "rgba(11,175,175,.18)" : "none"}
        stroke="#0BAFAF" strokeWidth="2"
      />
      {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="3" fill="#0b6b6b" />)}
      {(edgesM || []).map((e, i) => {
        if (!closed && i === pts.length - 1) return null;
        const a = pts[i], b = pts[(i + 1) % pts.length];
        return (
          <text key={i} x={(a[0] + b[0]) / 2} y={(a[1] + b[1]) / 2} fontSize="9" textAnchor="middle" fill="#333">
            {ftIn(e)}
          </text>
        );
      })}
    </svg>
  );
}

export function MeasurementPanel({ roomId }) {
  const [state, setState] = useState(null);
  const [images, setImages] = useState([]);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(true);

  const loadImages = useCallback(async () => {
    if (!roomId) return;
    try {
      const res = await fetch(`${API_BASE}/public/get-image?room_id=${encodeURIComponent(roomId)}`);
      const body = await res.json();
      if (!res.ok || !body.status) return;
      const list = (body.data || [])
        .map((im) => {
          const name = (im.img_url || "").split("/").pop();
          const ts = Number((name.match(/_(\d{13})\./) || [])[1] || 0);
          return { name, ts, url: im.signed_url };
        })
        .filter((im) => MEASURE_IMG.test(im.name))
        .sort((a, b) => b.ts - a.ts);
      setImages(list);
    } catch (e) {
      console.warn("gallery load failed", e);
    }
  }, [roomId]);

  const onMessage = useCallback((msg) => {
    let data;
    try { data = JSON.parse(msg.message); } catch (_) { return; }
    if (data.type === "state") {
      if (!latestState || (data.ts || 0) >= (latestState.ts || 0)) latestState = data;
      setState((prev) => (!prev || (data.ts || 0) >= (prev.ts || 0) ? data : prev));
    }
    if (data.type === "image") loadImages();
  }, [loadImages]);

  usePubSub("MEASUREMENT", {
    onMessageReceived: onMessage,
    onOldMessagesReceived: (msgs) => (msgs || []).forEach(onMessage),
  });

  useEffect(() => {
    loadImages();
    const t = setInterval(loadImages, 15000);
    window.addEventListener("cc-captures-changed", loadImages);
    return () => { clearInterval(t); window.removeEventListener("cc-captures-changed", loadImages); };
  }, [loadImages]);

  const rooms = useMemo(() => (state && state.rooms) || [], [state]);
  const cur = state && state.current;
  const totalM2 = rooms.reduce((a, r) => a + (r.areaM2 || 0), 0);
  const latest = images[0];

  const box = {
    position: "fixed", top: 72, right: 12, zIndex: 20, width: open ? 330 : "auto",
    maxHeight: "calc(100vh - 170px)", overflowY: "auto", background: "rgba(255,255,255,.97)",
    color: "#1b2733", borderRadius: 10, boxShadow: "0 4px 18px rgba(0,0,0,.25)", fontSize: 13,
  };

  return (
    <>
      <div style={box}>
        <div
          onClick={() => setOpen(!open)}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", cursor: "pointer", borderBottom: open ? "1px solid #e3e8ec" : 0 }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 4, background: state && state.active ? "#16a34a" : "#9aa5ae" }} />
          <b style={{ flex: 1 }}>Measurement</b>
          {state && (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: state.mode === "ar" ? "#dcfce7" : "#fde9cc", color: state.mode === "ar" ? "#166534" : "#8a5a00" }}>
              {state.mode === "ar" ? "AR" : "Estimated (no AR)"}
            </span>
          )}
          <span>{open ? "▾" : "▸"}</span>
        </div>

        {open && (
          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {!state && <div style={{ color: "#5b6670" }}>Waiting for the customer to start measuring…</div>}

            {cur && cur.corners > 0 && (
              <div style={{ display: "flex", gap: 10, alignItems: "center", background: "#eefbfb", padding: 8, borderRadius: 8 }}>
                <RoomShape poly={cur.poly} size={90} />
                <div>
                  <div><b>In progress</b> · {cur.corners} corner{cur.corners === 1 ? "" : "s"}</div>
                  {cur.areaM2 > 0 && <div>{sqft(cur.areaM2)} sq ft ({cur.areaM2.toFixed(2)} m²)</div>}
                </div>
              </div>
            )}

            {rooms.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", borderBottom: "1px solid #eef1f3", paddingBottom: 8 }}>
                <RoomShape poly={r.poly} edgesM={r.edgesM} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{r.name || `Room ${i + 1}`}</div>
                  <div>{sqft(r.areaM2)} sq ft</div>
                  <div style={{ color: "#5b6670" }}>{r.areaM2.toFixed(2)} m² · perimeter {ftIn(r.perimM || 0)}</div>
                </div>
              </div>
            ))}

            {rooms.length > 0 && (
              <div style={{ background: "#0BAFAF", color: "#fff", padding: "8px 10px", borderRadius: 8, fontWeight: 700 }}>
                Buildup Area{state.mode !== "ar" ? " (est.)" : ""}: {sqft(totalM2)} sq ft ({totalM2.toFixed(2)} m²)
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
              <b>Captures ({images.length})</b>
              <button type="button" onClick={loadImages} style={{ fontSize: 12, padding: "2px 8px", border: "1px solid #cfd8dc", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
                Refresh
              </button>
            </div>
            {latest && (
              <img
                src={latest.url} alt={latest.name} onClick={() => setPreview(latest)}
                style={{ width: "100%", borderRadius: 8, cursor: "zoom-in", background: "#000" }}
              />
            )}
            {images.length > 1 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                {images.slice(1, 13).map((im) => (
                  <img
                    key={im.name} src={im.url} alt={im.name} title={im.name} onClick={() => setPreview(im)}
                    style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 6, cursor: "zoom-in", background: "#000" }}
                  />
                ))}
              </div>
            )}
            {!images.length && <div style={{ color: "#5b6670" }}>No captures yet. They appear here as soon as the customer sends one.</div>}
          </div>
        )}
      </div>

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,.85)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20, cursor: "zoom-out" }}
        >
          <img src={preview.url} alt={preview.name} style={{ maxWidth: "95%", maxHeight: "88vh", borderRadius: 8 }} />
          <div style={{ color: "#fff", marginTop: 10, fontSize: 13 }}>
            {preview.name} · <a href={preview.url} target="_blank" rel="noreferrer" style={{ color: "#7fe0e0" }} onClick={(e) => e.stopPropagation()}>open full size</a>
          </div>
        </div>
      )}
    </>
  );
}

// Agent buttons: publish on the call's own PubSub (reaches the web customer app) and
// keep the existing Ably message (reaches the native APK).
// webOnly: the command exists only in the web customer app, so don't send it to the APK.
export function useMeasureCommand(ablyChannel) {
  const { publish } = usePubSub("MEASUREMENT_CMD");
  return useCallback((cmd, { webOnly = false } = {}) => {
    try { publish(cmd, { persist: false }); } catch (e) { console.warn("pubsub publish failed", e); }
    if (webOnly) return;
    try { ablyChannel && ablyChannel.publish("first", cmd); } catch (e) { console.warn("ably publish failed", e); }
  }, [publish, ablyChannel]);
}
